import React, { useCallback, useEffect, useState } from 'react';

interface UseDraggableYResult {
  position: { top: number | string } | undefined;
  handleMouseDown: (e: React.MouseEvent) => void;
  isDragging: boolean;
  isDrag: boolean;
}

export const useDraggableY = (initialTop?: number): UseDraggableYResult => {
  const [position, setPosition] = useState<{ top: number | string } | undefined>(undefined);
  const [isDragging, setIsDragging] = useState(false);
  const [dragOffset, setDragOffset] = useState(0);
  const [startPos, setStartPos] = useState(0);
  const [isDrag, setIsDrag] = useState(false);

  const handleMouseDown = useCallback(
    (e: React.MouseEvent) => {
      // Only left click
      if (e.button !== 0) return;

      // Prevent default text selection during drag
      // e.preventDefault(); // This might block focus on inputs if any, but valid for buttons.

      const element = e.currentTarget as HTMLElement;
      const rect = element.getBoundingClientRect();
      const clientY = e.clientY;

      setDragOffset(clientY - rect.top);
      setStartPos(clientY);
      setIsDragging(true);
      setIsDrag(false);

      // Initialize position on first interaction to current actual position
      // This allows transition from CSS '20%' or '130px' to explicit pixels
      if (position === undefined) {
        setPosition({ top: rect.top });
      }
    },
    [position]
  );

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (isDragging) {
        const currentY = e.clientY;
        const delta = Math.abs(currentY - startPos);

        // Threshold to consider it a drag operation (vs a click)
        if (delta > 5) {
          setIsDrag(true);
        }

        const newTop = currentY - dragOffset;
        const maxTop = window.innerHeight - 50; // Safety margin
        const clampedTop = Math.max(0, Math.min(newTop, maxTop));

        setPosition({ top: clampedTop });
      }
    };

    const handleMouseUp = () => {
      setIsDragging(false);
    };

    if (isDragging) {
      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('mouseup', handleMouseUp);
    }

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging, dragOffset, startPos]);

  return { position, handleMouseDown, isDragging, isDrag };
};
